import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { BlogManager } from '../components/blog/BlogManager';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ShareProfileDialog } from '../components/ShareProfileDialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Activity,
  Calendar as CalendarIcon,
  User,
  FileText,
  Upload,
  LogOut,
  Clock,
  Video,
  Home,
  Building,
  Bell,
  Settings,
  Menu,
  X,
  Loader2,
  CheckCircle,
  XCircle,
  DollarSign,
  Users,
  AlertTriangle,
  Save,
  ChevronRight,
  Eye,
  MapPin,
  Camera,
  BarChart3,
  TrendingUp,
  IndianRupee,
  Stethoscope,
  ArrowUpRight,
  ArrowDownRight,
  Power,
  Wallet,
  ShieldCheck,
  Sparkles,
  ArrowRight,
  Briefcase,
  GraduationCap,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiRequest, formatDate, getInitials } from '../lib/utils';
import { DoctorAppointmentCard } from '../components/DoctorAppointmentCard';
import { NurseServicesForm } from '../components/NurseServicesForm';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { DOCTOR_SPECIALIZATIONS as specializations, NURSE_SPECIALIZATIONS as nurseSpecializations } from '../constants/specializations';

const PHYSIGN_LOGO = "https://customer-assets.emergentagent.com/job_4bf5f59f-ff2a-4b89-ab5f-09ffb3b85c36/artifacts/91dqnx5f_high-resolution-color-logo-3.png";

const nurseQualifications = [
  { value: 'anm', label: 'ANM (Auxiliary Nurse Midwifery)' },
  { value: 'gnm', label: 'GNM (General Nursing & Midwifery)' },
  { value: 'bsc_nursing', label: 'BSc Nursing' },
  { value: 'msc_nursing', label: 'MSc Nursing' },
  { value: 'post_basic_nursing', label: 'Post Basic BSc Nursing' },
];

const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function ProviderDashboard() {
  const navigate = useNavigate();
  const { user, profile, logout, isAuthenticated, loading: authLoading, updateProfile } = useAuth();
  
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.tab || 'overview');
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [selectedApt, setSelectedApt] = useState(null);
  const [isAvailable, setIsAvailable] = useState(true);
  const [togglingAvailability, setTogglingAvailability] = useState(false);
  
  // Track uploaded document URLs for preview
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [nurseServices, setNurseServices] = useState([{ name: '', price: '', pricing_type: '' }]);
  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  // Detect if provider needs onboarding
  const isNewProvider = !user?.name || !profile?.specialization;
  const onboardingComplete = user?.name && profile?.specialization && (
    user?.role === 'doctor'
      ? profile?.medical_registration_number
      : profile?.license_number
  );
  const hasDocuments = profile?.license_document || profile?.id_proof_document;
  
  // Profile form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    specialization: '',
    qualification: '',
    experience_years: '',
    license_number: '',
    //medical_registration_number: '',
    bio: '',
    consultation_fee: '',
    home_visit_fee: '',
    location: '',
    clinic_address: '',
    clinic_name: '',
    services: [],
    available_days: [],
    time_slots: [{ start: '09:00', end: '22:00' }],
    // Nurse-specific fields
    father_husband_name: '',
    date_of_birth: '',
    gender: '',
    current_address: '',
    permanent_address: '',
    state_nursing_council: '',
    current_hospital: '',
    preferred_area: '',
    /*bank_account_holder: '',
    bank_name: '',
    account_number: '',
    ifsc_code: '',*/
    declaration_accepted: false,
  });

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/auth');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated && user && ['doctor', 'nurse', 'lab'].includes(user.role)) {
      fetchAppointments();
      setIsAvailable(user.is_active !== false);
      
      // Auto-redirect new providers to profile tab
      if (isNewProvider && !location.state?.tab) {
        setActiveTab('profile');
        setOnboardingStep(1);
      }
      
      // Populate form with existing data
      let nurseData = {};
      let bioText = profile?.bio || '';
      
      // Parse nurse-specific data from bio field
      if (user.role === 'nurse' && profile?.bio) {
        try {
          const parsed = JSON.parse(profile.bio);
          if (parsed.nurse_data) {
            nurseData = parsed.nurse_data;
            bioText = parsed.bio_text || '';
          }
        } catch {
          bioText = profile.bio;
        }
      }
      
      // Load nurse services
      if (nurseData.nurse_services && nurseData.nurse_services.length > 0) {
        setNurseServices(nurseData.nurse_services);
      }
      
      setFormData({
        name: user.name || '',
        email: user.email || '',
        specialization: profile?.specialization || '',
        qualification: profile?.qualification || '',
        experience_years: profile?.experience_years?.toString() || '',
        license_number: profile?.license_number || '',
       // medical_registration_number: profile?.medical_registration_number || '',
        bio: bioText,
        consultation_fee: profile?.consultation_fee?.toString() || '500',
        home_visit_fee: profile?.home_visit_fee?.toString() || '1000',
        location: profile?.location || '',
        clinic_address: profile?.clinic_address || '',
        clinic_name: profile?.clinic_name || '',
        services: profile?.services || (user.role === 'nurse' ? ['home_visit'] : ['clinic', 'home_visit']),
        available_days: profile?.available_days || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat','Sun' ],
        time_slots: profile?.time_slots?.length ? profile.time_slots : [{ start: '09:00', end: '22:00' }],
        // Nurse-specific
        //father_husband_name: nurseData.father_husband_name || '',
        date_of_birth: nurseData.date_of_birth || '',
        gender: nurseData.gender || '',
        current_address: nurseData.current_address || '',
        //permanent_address: nurseData.permanent_address || '',
        state_nursing_council: nurseData.state_nursing_council || '',
        current_hospital: nurseData.current_hospital || '',
        preferred_area: nurseData.preferred_area || '',
        //bank_account_holder: nurseData.bank_account_holder || '',
        //bank_name: nurseData.bank_name || '',
        //account_number: nurseData.account_number || '',
        //ifsc_code: nurseData.ifsc_code || '',
        declaration_accepted: nurseData.declaration_accepted || false,
      });
    }
  }, [isAuthenticated, user, profile]);

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const [aptsData, notifsData] = await Promise.all([
        apiRequest('/appointments'),
        apiRequest('/notifications'),
      ]);
      setAppointments(aptsData.appointments || []);
      setNotifications(notifsData.notifications || []);
      setUnreadCount(notifsData.unread_count || 0);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await apiRequest('/notifications/read-all', { method: 'PUT' });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error(error);
    }
  };

  const handleToggleAvailability = async () => {
    setTogglingAvailability(true);
    try {
      const data = await apiRequest('/provider/availability', { method: 'PUT' });
      setIsAvailable(data.is_available);
      toast.success(data.is_available ? 'You are now Available!' : 'You are now Unavailable');
    } catch (error) {
      toast.error('Failed to update availability');
    } finally {
      setTogglingAvailability(false);
    }
  };

  const handleUpdateStatus = async (appointmentId, status) => {
    try {
      await apiRequest(`/appointments/${appointmentId}/status?status=${status}`, {
        method: 'PUT',
      });
      toast.success(`Appointment ${status}`);
      fetchAppointments();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const saveData = {
        ...formData,
        experience_years: parseInt(formData.experience_years) || 0,
        consultation_fee: parseFloat(formData.consultation_fee) || 0,
        home_visit_fee: parseFloat(formData.home_visit_fee) || 0,
      };
      // Include nurse services in save data for nurses
      if (user?.role === 'nurse') {
        saveData.nurse_services = nurseServices.filter(s => s.name && s.price && s.pricing_type);
      }
      await updateProfile(saveData);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (docType, file) => {
    if (!file) return;
    
    const formDataObj = new FormData();
    formDataObj.append('file', file);
    
    try {
      const result = await apiRequest(`/provider/upload-document?doc_type=${docType}`, {
        method: 'POST',
        body: formDataObj,
        headers: {},
      });
      setUploadedDocs(prev => ({ ...prev, [docType]: result.file_url }));
      toast.success('Document uploaded successfully');
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Get document preview URL - handles full Supabase URLs + legacy local paths
  const getDocUrl = (docType) => {
    // Helper to fix URL path
    const fixUrl = (url) => {
      if (!url) return null;
      // Full Supabase Storage URL — return as-is
      if (url.startsWith('http://') || url.startsWith('https://')) return url;
      // Convert old /uploads/ paths to /api/uploads/
      if (url.startsWith('/uploads/')) {
        return `${API_BASE}/api${url}`;
      }
      // New paths already have /api prefix
      return `${API_BASE}${url}`;
    };
    
    // Check local upload first, then profile data
    if (uploadedDocs[docType]) return fixUrl(uploadedDocs[docType]);
    if (docType === 'license' && profile?.license_document) return fixUrl(profile.license_document);
    if (docType === 'id_proof' && profile?.id_proof_document) return fixUrl(profile.id_proof_document);
    if (docType === 'certificate' && profile?.certificates?.[0]) return fixUrl(profile.certificates[0]);
    if (docType === 'registration_cert' && profile?.certificates?.[1]) return fixUrl(profile.certificates[1]);
    if (docType === 'photo' && user?.avatar) return fixUrl(user.avatar);
    return null;
  };

  const isDocUploaded = (docType) => {
    if (uploadedDocs[docType]) return true;
    if (docType === 'license' && profile?.license_document) return true;
    if (docType === 'id_proof' && profile?.id_proof_document) return true;
    if (docType === 'certificate' && profile?.certificates?.[0]) return true;
    if (docType === 'registration_cert' && profile?.certificates?.[1]) return true;
    if (docType === 'photo' && user?.avatar) return true;
    return false;
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const toggleService = (service) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const toggleDay = (day) => {
    setFormData(prev => ({
      ...prev,
      available_days: prev.available_days.includes(day)
        ? prev.available_days.filter(d => d !== day)
        : [...prev.available_days, day]
    }));
  };

  const pendingAppointments = appointments.filter(a => a.status === 'pending');
  const confirmedAppointments = appointments.filter(a => a.status === 'confirmed');
  const completedAppointments = appointments.filter(a => a.status === 'completed');

  const totalEarnings = completedAppointments.reduce((sum, apt) => sum + (apt.fee || 0), 0);

  // Enhanced Stats
  const todayStr = new Date().toISOString().split('T')[0];
  const todayAppointments = appointments.filter(a => a.appointment_date === todayStr);
  const todayPending = todayAppointments.filter(a => a.status === 'pending').length;
  const todayConfirmed = todayAppointments.filter(a => a.status === 'confirmed').length;
  const todayCompleted = todayAppointments.filter(a => a.status === 'completed').length;
  const todayEarnings = todayAppointments.filter(a => a.status === 'completed').reduce((s, a) => s + (a.fee || 0), 0);

  // This month earnings
  const monthStr = todayStr.slice(0, 7);
  const monthCompleted = appointments.filter(a => a.status === 'completed' && a.appointment_date?.startsWith(monthStr));
  const monthEarnings = monthCompleted.reduce((s, a) => s + (a.fee || 0), 0);

  // Unique patients
  const uniquePatients = new Set(appointments.map(a => a.patient_id)).size;
  const thisMonthPatients = new Set(appointments.filter(a => a.appointment_date?.startsWith(monthStr)).map(a => a.patient_id)).size;

  // Top booked services
  const serviceCounts = {};
  appointments.forEach(apt => {
    let svcName = apt.service_type === 'home_visit' ? 'Home Visit' : apt.service_type === 'clinic' ? 'Clinic Visit' : apt.service_type || 'Other';
    if (apt.symptoms?.startsWith('Service: ')) {
      svcName = apt.symptoms.split('Service: ')[1].split(' |')[0].trim();
    }
    serviceCounts[svcName] = (serviceCounts[svcName] || 0) + 1;
  });
  const topServices = Object.entries(serviceCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);

  // Weekly earnings (last 7 days)
  const last7Days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const ds = d.toISOString().split('T')[0];
    const dayEarn = appointments.filter(a => a.appointment_date === ds && a.status === 'completed').reduce((s, a) => s + (a.fee || 0), 0);
    const dayCount = appointments.filter(a => a.appointment_date === ds).length;
    last7Days.push({ date: ds, day: ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()], earnings: dayEarn, count: dayCount });
  }

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#0F62FE]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="provider-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button 
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              <Link to="/" className="flex items-center gap-2">
                <span className="hidden sm:block text-xl font-bold text-[#3B1C73]" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                  Physign
                </span>
              </Link>
            </div>

            <div className="flex items-center gap-3">
              {/* Availability Toggle */}
              <button
                onClick={handleToggleAvailability}
                disabled={togglingAvailability}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-300 border-2 ${
                  isAvailable 
                    ? 'bg-emerald-50 border-emerald-400 text-emerald-700 hover:bg-emerald-100' 
                    : 'bg-red-50 border-red-300 text-red-600 hover:bg-red-100'
                }`}
                data-testid="availability-toggle"
              >
                {togglingAvailability ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Power className={`w-3.5 h-3.5 ${isAvailable ? 'text-emerald-500' : 'text-red-400'}`} />
                )}
                <span className="hidden sm:inline">{isAvailable ? 'Available' : 'Unavailable'}</span>
                <div className={`w-8 h-4 rounded-full relative transition-colors duration-300 ${isAvailable ? 'bg-emerald-400' : 'bg-red-300'}`}>
                  <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow-sm transition-transform duration-300 ${isAvailable ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </div>
              </button>

              {/* Approval Status Badge */}
              {profile?.approval_status === 'pending' && (
                <Badge className="bg-amber-100 text-amber-800">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Pending Approval
                </Badge>
              )}
              {profile?.approval_status === 'approved' && (
                <Badge className="bg-emerald-100 text-emerald-800">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Verified
                </Badge>
              )}

              <div className="relative">
                <Button variant="ghost" size="icon" className="relative" onClick={() => setShowNotifications(!showNotifications)} data-testid="provider-notification-bell">
                  <Bell className="w-5 h-5 text-slate-600" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 rounded-full text-white text-[10px] flex items-center justify-center font-bold px-1">
                      {unreadCount}
                    </span>
                  )}
                </Button>
                {showNotifications && (
                  <div className="absolute right-0 top-12 w-80 bg-white rounded-xl shadow-xl border border-slate-200 z-50 max-h-96 overflow-hidden" data-testid="provider-notifications-dropdown">
                    <div className="p-3 border-b flex items-center justify-between">
                      <h3 className="font-semibold text-sm">Notifications</h3>
                      {unreadCount > 0 && (
                        <button className="text-xs text-blue-600 hover:underline" onClick={handleMarkAllRead}>Mark all read</button>
                      )}
                    </div>
                    <div className="overflow-y-auto max-h-72">
                      {notifications.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-6">No notifications</p>
                      ) : (
                        notifications.map(n => (
                          <div key={n.id} className={`p-3 border-b border-slate-50 text-sm ${n.is_read ? 'bg-white' : 'bg-blue-50'}`}>
                            <p className="font-medium text-slate-800">{n.title}</p>
                            <p className="text-slate-500 text-xs mt-0.5">{n.message}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[#E0F2FE] flex items-center justify-center text-sm font-medium text-[#0F62FE]">
                      {getInitials(user?.name)}
                    </div>
                    <span className="hidden sm:block text-sm font-medium capitalize">
                      {user?.name || user?.role}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setActiveTab('profile')}>
                    <User className="w-4 h-4 mr-2" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Verification / Onboarding Hero Card */}
        {profile?.onboarding_completed !== true ? (
          <div className="mb-6 relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#3B1C73] to-[#5B2FAA] text-white p-6 shadow-lg" data-testid="onboarding-cta-card">
            <div className="absolute -right-8 -top-8 opacity-10">
              <Sparkles className="w-40 h-40" />
            </div>
            <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-white/15 rounded-full text-xs font-semibold uppercase tracking-wider mb-2">
                  <ShieldCheck className="w-3.5 h-3.5" /> Action Required
                </div>
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'Plus Jakarta Sans' }}>Complete Your Onboarding</h2>
                <p className="text-white/80 mt-1 text-sm max-w-xl">
                  Finish your 8-step verification to unlock patient bookings, receive payouts, and earn a verified badge.
                </p>
              </div>
              <button
                onClick={() => navigate('/provider/onboarding')}
                className="inline-flex items-center gap-2 px-5 py-3 bg-white text-[#3B1C73] rounded-xl font-semibold shadow-md hover:shadow-lg hover:scale-105 transition-all"
                data-testid="continue-onboarding-btn"
              >
                Continue Onboarding <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : profile?.approval_status === 'pending' ? (
          <div className="mb-6 p-5 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl flex items-start gap-4" data-testid="verification-pending-card">
            <div className="w-11 h-11 rounded-xl bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
              <Clock className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-amber-900 text-base">Verification Under Review</h3>
              <p className="text-sm text-amber-800 mt-0.5">
                Great! Your documents are with our team. Verification usually completes within 24 hours — you&apos;ll receive an email/SMS once approved.
              </p>
            </div>
          </div>
        ) : profile?.approval_status === 'approved' ? (
          <div className="mb-6 p-5 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-2xl flex flex-wrap sm:flex-nowrap items-start gap-4" data-testid="verification-approved-card">
            <div className="w-11 h-11 rounded-xl bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-emerald-900 text-base flex items-center gap-2">Verified Doctor <Sparkles className="w-4 h-4 text-emerald-600" /></h3>
              <p className="text-sm text-emerald-800 mt-0.5">
                Your profile is live. Patients can find and book you. Keep your availability updated for maximum bookings.
              </p>
            </div>
            <ShareProfileDialog
              providerId={user?.id}
              name={user?.name}
              specialization={profile?.specialization}
              role={user?.role}
              triggerLabel="Share my profile"
              triggerClassName="bg-white border-emerald-300 text-emerald-800 hover:bg-emerald-100 flex-shrink-0"
              testId="dashboard-share-profile-btn"
            />
          </div>
        ) : null}

        {/* Today's Earnings + Wallet Hero Row (only for onboarded providers) */}
        {profile?.onboarding_completed === true && (
          <div className="grid md:grid-cols-2 gap-4 mb-6" data-testid="earnings-wallet-row">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#3B1C73] to-[#5B2FAA] text-white p-5 shadow-md">
              <div className="absolute -right-6 -bottom-6 opacity-10">
                <IndianRupee className="w-32 h-32" />
              </div>
              <div className="relative">
                <div className="flex items-center gap-2 text-white/70 text-xs font-semibold uppercase tracking-wider">
                  <TrendingUp className="w-3.5 h-3.5" /> Today&apos;s Earnings
                </div>
                <p className="text-4xl font-bold mt-2" data-testid="hero-today-earnings">₹{todayEarnings.toLocaleString('en-IN')}</p>
                <div className="flex items-center gap-3 mt-3 text-sm text-white/85">
                  <span>{todayCompleted} completed</span>
                  <span className="w-1 h-1 bg-white/40 rounded-full"></span>
                  <span>{todayAppointments.length} total today</span>
                </div>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wider">
                    <Wallet className="w-3.5 h-3.5" /> Wallet Balance
                  </div>
                  <p className="text-4xl font-bold text-slate-900 mt-2" data-testid="hero-wallet-balance">₹{totalEarnings.toLocaleString('en-IN')}</p>
                  <p className="text-xs text-slate-500 mt-3">
                    {completedAppointments.length} completed appointment{completedAppointments.length !== 1 ? 's' : ''} total
                  </p>
                </div>
                <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <Wallet className="w-5 h-5 text-emerald-600" />
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                <div className="text-xs">
                  <span className="text-slate-500">Payouts: </span>
                  <span className="font-medium text-slate-700">Coming Soon</span>
                </div>
                <span className="text-xs text-slate-400 italic">Razorpay Route integration in progress</span>
              </div>
            </div>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview" data-testid="overview-tab">Overview</TabsTrigger>
            <TabsTrigger value="appointments" data-testid="appointments-tab">Appointments</TabsTrigger>
            <TabsTrigger value="profile" data-testid="profile-tab">Profile</TabsTrigger>
            {user?.role === 'doctor' && <TabsTrigger value="blogs" data-testid="blogs-tab">Blogs</TabsTrigger>}
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {/* Top Stats Row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="overview-stats-row">
              <Card className="border-l-4 border-l-amber-400">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Today's Appointments</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="stat-today-appointments">{todayAppointments.length}</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-xs px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">{todayPending} pending</span>
                        <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">{todayConfirmed} confirmed</span>
                      </div>
                    </div>
                    <div className="w-11 h-11 rounded-xl bg-amber-50 flex items-center justify-center">
                      <Stethoscope className="w-5 h-5 text-amber-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-emerald-400">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Monthly Revenue</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="stat-monthly-revenue">₹{monthEarnings.toLocaleString('en-IN')}</p>
                      <p className="text-xs text-slate-500 mt-1.5">{monthCompleted.length} completed this month</p>
                    </div>
                    <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center">
                      <IndianRupee className="w-5 h-5 text-emerald-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-blue-400">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total Patients</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="stat-total-patients">{uniquePatients}</p>
                      <p className="text-xs text-slate-500 mt-1.5">{thisMonthPatients} this month</p>
                    </div>
                    <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center">
                      <Users className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-purple-400">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total Earnings</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="stat-total-earnings">₹{totalEarnings.toLocaleString('en-IN')}</p>
                      <p className="text-xs text-slate-500 mt-1.5">{completedAppointments.length} completed total</p>
                    </div>
                    <div className="w-11 h-11 rounded-xl bg-purple-50 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Appointment Status Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="appointment-status-summary">
              <div className="flex items-center gap-2.5 p-3 rounded-xl bg-amber-50 border border-amber-100">
                <Clock className="w-4 h-4 text-amber-500 shrink-0" />
                <div>
                  <p className="text-lg font-bold text-slate-900 leading-tight">{pendingAppointments.length}</p>
                  <p className="text-xs text-amber-600">Pending</p>
                </div>
              </div>
              <div className="flex items-center gap-2.5 p-3 rounded-xl bg-blue-50 border border-blue-100">
                <CalendarIcon className="w-4 h-4 text-blue-500 shrink-0" />
                <div>
                  <p className="text-lg font-bold text-slate-900 leading-tight">{confirmedAppointments.length}</p>
                  <p className="text-xs text-blue-600">Confirmed</p>
                </div>
              </div>
              <div className="flex items-center gap-2.5 p-3 rounded-xl bg-emerald-50 border border-emerald-100">
                <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                <div>
                  <p className="text-lg font-bold text-slate-900 leading-tight">{completedAppointments.length}</p>
                  <p className="text-xs text-emerald-600">Completed</p>
                </div>
              </div>
              <div className="flex items-center gap-2.5 p-3 rounded-xl bg-red-50 border border-red-100">
                <XCircle className="w-4 h-4 text-red-500 shrink-0" />
                <div>
                  <p className="text-lg font-bold text-slate-900 leading-tight">{appointments.filter(a => a.status === 'cancelled').length}</p>
                  <p className="text-xs text-red-600">Cancelled</p>
                </div>
              </div>
            </div>

            {/* Middle Row: Weekly Trend + Top Services */}
            <div className="grid lg:grid-cols-2 gap-6 mb-6">
              {/* Weekly Earnings Chart */}
              <Card data-testid="weekly-earnings-chart">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-[#0F62FE]" />
                    Last 7 Days
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2.5">
                    {last7Days.map((d, i) => {
                      const maxEarn = Math.max(...last7Days.map(x => x.earnings), 1);
                      const barWidth = d.earnings > 0 ? Math.max((d.earnings / maxEarn) * 100, 8) : 0;
                      const isToday = i === last7Days.length - 1;
                      return (
                        <div key={d.date} className="flex items-center gap-3">
                          <span className={`text-xs w-8 font-medium ${isToday ? 'text-[#0F62FE] font-bold' : 'text-slate-500'}`}>{d.day}</span>
                          <div className="flex-1 h-7 bg-slate-50 rounded-lg overflow-hidden relative">
                            {barWidth > 0 && (
                              <div
                                className={`h-full rounded-lg transition-all duration-500 ${isToday ? 'bg-[#0F62FE]' : 'bg-blue-200'}`}
                                style={{ width: `${barWidth}%` }}
                              />
                            )}
                          </div>
                          <div className="text-right w-20">
                            <span className="text-xs font-semibold text-slate-700">₹{d.earnings.toLocaleString('en-IN')}</span>
                            <span className="text-[10px] text-slate-400 ml-1">({d.count})</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Top Booked Services */}
              <Card data-testid="top-services-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Activity className="w-4 h-4 text-emerald-500" />
                    Top Booked Services
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {topServices.length === 0 ? (
                    <div className="text-center py-6">
                      <BarChart3 className="w-10 h-10 text-slate-200 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">No bookings yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {topServices.map(([name, count], idx) => {
                        const maxCount = topServices[0][1];
                        const barW = Math.max((count / maxCount) * 100, 12);
                        const colors = ['bg-[#0F62FE]', 'bg-emerald-400', 'bg-amber-400', 'bg-purple-400', 'bg-pink-400'];
                        return (
                          <div key={name} className="flex items-center gap-3">
                            <span className="w-5 text-xs font-bold text-slate-400">#{idx + 1}</span>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium text-slate-700 capitalize">{name}</span>
                                <span className="text-xs font-semibold text-slate-500">{count} bookings</span>
                              </div>
                              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${colors[idx % colors.length]}`} style={{ width: `${barW}%` }} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Pending Requests */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-amber-500" />
                  Pending Requests
                  {pendingAppointments.length > 0 && (
                    <Badge className="bg-amber-100 text-amber-700 ml-2">{pendingAppointments.length}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-[#0F62FE]" />
                  </div>
                ) : pendingAppointments.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-12 h-12 text-emerald-200 mx-auto mb-3" />
                    <p className="text-slate-500">All caught up! No pending requests.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingAppointments.map((apt) => (
                      <DoctorAppointmentCard key={apt.id} apt={apt} onAction={handleUpdateStatus} profile={profile} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Today's Schedule */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5 text-blue-500" />
                  Today's Schedule
                  {todayAppointments.length > 0 && (
                    <Badge className="bg-blue-100 text-blue-700 ml-2">{todayAppointments.length}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {todayAppointments.filter(a => a.status === 'confirmed').length === 0 ? (
                  <div className="text-center py-8">
                    <CalendarIcon className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                    <p className="text-slate-500">No confirmed appointments for today</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {todayAppointments.filter(a => a.status === 'confirmed').map((apt) => (
                      <DoctorAppointmentCard key={apt.id} apt={apt} onAction={handleUpdateStatus} profile={profile} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Appointments Tab */}
          <TabsContent value="appointments">
            <Card>
              <CardHeader>
                <CardTitle>All Appointments</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-[#0F62FE]" />
                  </div>
                ) : appointments.length === 0 ? (
                  <div className="text-center py-8">
                    <CalendarIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-600">No appointments yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {appointments.map((apt) => (
                      <DoctorAppointmentCard key={apt.id} apt={apt} onAction={handleUpdateStatus} profile={profile} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            {/* Quick Edit Shortcuts (post-onboarding — jump straight to any wizard step) */}
            {profile?.onboarding_completed === true && (
              <Card className="mb-6 border-purple-100 bg-gradient-to-br from-purple-50/40 to-white" data-testid="profile-edit-shortcuts">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div>
                      <h3 className="font-semibold text-slate-900 text-base" style={{ fontFamily: 'Plus Jakarta Sans' }}>Manage Your Profile</h3>
                      <p className="text-xs text-slate-500 mt-0.5">Update any section anytime. Changes take effect immediately.</p>
                    </div>
                    <button
                      onClick={() => navigate('/provider/onboarding')}
                      className="text-xs text-[#3B1C73] hover:underline font-medium flex items-center gap-1"
                      data-testid="edit-full-profile-btn"
                    >
                      Edit Full Profile <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[
                      { step: 1, label: 'Personal', icon: User },
                      { step: 2, label: 'Professional', icon: Stethoscope },
                      { step: 4, label: 'Practice', icon: MapPin },
                      { step: 5, label: 'Availability', icon: CalendarIcon },
                      { step: 6, label: 'Experience', icon: Briefcase },
                      { step: 7, label: 'Documents & Bank', icon: FileText },
                      { step: 3, label: 'Education', icon: GraduationCap },
                      { step: 8, label: 'Review', icon: ShieldCheck },
                    ].map(({ step, label, icon: Icon }) => (
                      <button
                        key={step}
                        onClick={() => navigate(`/provider/onboarding?step=${step}`)}
                        className="p-3 bg-white border border-slate-200 rounded-lg text-left hover:border-[#3B1C73] hover:shadow-sm transition-all group"
                        data-testid={`edit-shortcut-${step}`}
                      >
                        <Icon className="w-4 h-4 text-slate-400 group-hover:text-[#3B1C73] mb-1.5" />
                        <p className="text-xs font-semibold text-slate-900">{label}</p>
                        <p className="text-[10px] text-slate-500 mt-0.5">Edit</p>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Profile Image Upload - Common for Doctor & Nurse */}
            <Card className="mb-6 overflow-hidden">
              {/* Cover Image Section */}
              <div className="relative">
                <div
                  className="h-32 sm:h-40 bg-gradient-to-br from-[#3B1C73] to-[#5B2FAA] relative"
                  data-testid="cover-image-container"
                >
                  {profile?.cover_image && (
                    <img
                      src={profile.cover_image}
                      alt="Cover"
                      className="w-full h-full object-cover"
                      data-testid="cover-image"
                    />
                  )}
                  <label className="absolute top-3 right-3 px-3 py-1.5 bg-white/90 hover:bg-white text-slate-700 rounded-lg text-xs font-medium cursor-pointer flex items-center gap-1.5 shadow-sm transition-colors" data-testid="upload-cover-btn">
                    <Camera className="w-3.5 h-3.5" />
                    {profile?.cover_image ? 'Change Cover' : 'Upload Cover'}
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp"
                      className="hidden"
                      onChange={(e) => handleUpload('cover_image', e.target.files[0])}
                      data-testid="cover-file-input"
                    />
                  </label>
                </div>
              </div>
              <CardContent className="p-6 -mt-10 relative z-10">
                <div className="flex items-end gap-6">
                  <div className="relative group">
                    <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-white bg-slate-50 flex items-center justify-center shadow-md" data-testid="profile-avatar">
                      {(uploadedDocs['photo'] || user?.avatar) ? (
                        <img
                          src={uploadedDocs['photo'] ? `${API_BASE}${uploadedDocs['photo']}` : (user?.avatar?.startsWith('/') ? `${API_BASE}/api${user.avatar.replace(/^\/api/, '')}` : `${API_BASE}/api/uploads/${user.avatar}`)}
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="w-10 h-10 text-slate-300" />
                      )}
                    </div>
                    <label className="absolute bottom-0 right-0 w-8 h-8 bg-[#0F62FE] rounded-full flex items-center justify-center cursor-pointer hover:bg-[#0050E6] transition-colors shadow-lg" data-testid="upload-avatar-btn">
                      <Camera className="w-4 h-4 text-white" />
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp"
                        className="hidden"
                        onChange={(e) => handleUpload('photo', e.target.files[0])}
                        data-testid="avatar-file-input"
                      />
                    </label>
                  </div>
                  <div className="pb-2">
                    <h3 className="text-lg font-semibold text-slate-900">{user?.name || 'Provider'}</h3>
                    <p className="text-sm text-slate-500 capitalize">{user?.role} {profile?.specialization ? `• ${profile.specialization}` : ''}</p>
                    <p className="text-xs text-slate-400 mt-1">Recommended: 1500×400px cover, 400×400px avatar</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {user?.role === 'nurse' ? (
              /* ============ NURSE REGISTRATION FORM ============ */
              <div className="space-y-6" data-testid="nurse-registration-form">
                {/* Section 1: Personal Details */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-[#0F62FE]">
                      <User className="w-5 h-5" />
                      1. Personal Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="nurse-name">Full Name *</Label>
                        <Input id="nurse-name" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="mt-1" placeholder="Enter full name" data-testid="nurse-name" />
                      </div>
                      <div>
                        <Label htmlFor="nurse-father">Father / Husband Name</Label>
                        <Input id="nurse-father" value={formData.father_husband_name} onChange={e => setFormData({...formData, father_husband_name: e.target.value})} className="mt-1" placeholder="Enter father/husband name" data-testid="nurse-father-name" />
                      </div>
                      <div>
                        <Label htmlFor="nurse-dob">Date of Birth</Label>
                        <Input id="nurse-dob" type="date" value={formData.date_of_birth} onChange={e => setFormData({...formData, date_of_birth: e.target.value})} className="mt-1" data-testid="nurse-dob" />
                      </div>
                      <div>
                        <Label htmlFor="nurse-gender">Gender</Label>
                        <Select value={formData.gender} onValueChange={v => setFormData({...formData, gender: v})}>
                          <SelectTrigger className="mt-1" data-testid="nurse-gender"><SelectValue placeholder="Select gender" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="female">Female</SelectItem>
                            <SelectItem value="male">Male</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="nurse-phone">Mobile Number</Label>
                        <Input id="nurse-phone" value={user?.phone || ''} disabled className="mt-1 bg-slate-50" />
                      </div>
                      <div>
                        <Label htmlFor="nurse-email">Email ID</Label>
                        <Input id="nurse-email" type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="mt-1" placeholder="email@example.com" data-testid="nurse-email" />
                      </div>
                      <div className="md:col-span-2">
                        <Label htmlFor="nurse-current-addr">Current Address</Label>
                        <Textarea id="nurse-current-addr" value={formData.current_address} onChange={e => setFormData({...formData, current_address: e.target.value})} className="mt-1" rows={2} placeholder="Current residential address" data-testid="nurse-current-address" />
                      </div>
                      <div className="md:col-span-2">
                        <Label htmlFor="nurse-perm-addr">Permanent Address</Label>
                        <Textarea id="nurse-perm-addr" value={formData.permanent_address} onChange={e => setFormData({...formData, permanent_address: e.target.value})} className="mt-1" rows={2} placeholder="Permanent address" data-testid="nurse-permanent-address" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Section 2: Professional Details */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-[#0F62FE]">
                      <FileText className="w-5 h-5" />
                      2. Professional Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Qualification *</Label>
                        <Select value={formData.qualification} onValueChange={v => setFormData({...formData, qualification: v})}>
                          <SelectTrigger className="mt-1" data-testid="nurse-qualification"><SelectValue placeholder="Select qualification" /></SelectTrigger>
                          <SelectContent>
                            {nurseQualifications.map(q => (
                              <SelectItem key={q.value} value={q.value}>{q.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="nurse-reg">Registration Number *</Label>
                        <Input id="nurse-reg" value={formData.license_number} onChange={e => setFormData({...formData, license_number: e.target.value})} className="mt-1" placeholder="Nursing registration number" data-testid="nurse-reg-number" />
                      </div>
                      <div>
                        <Label htmlFor="nurse-council">State Nursing Council Name</Label>
                        <Input id="nurse-council" value={formData.state_nursing_council} onChange={e => setFormData({...formData, state_nursing_council: e.target.value})} className="mt-1" placeholder="e.g. Maharashtra Nursing Council" data-testid="nurse-council" />
                      </div>
                      <div>
                        <Label htmlFor="nurse-exp">Years of Experience</Label>
                        <Input id="nurse-exp" type="number" value={formData.experience_years} onChange={e => setFormData({...formData, experience_years: e.target.value})} className="mt-1" data-testid="nurse-experience" />
                      </div>
                      <div>
                        <Label htmlFor="nurse-hospital">Current Working Hospital / Clinic</Label>
                        <Input id="nurse-hospital" value={formData.current_hospital} onChange={e => setFormData({...formData, current_hospital: e.target.value})} className="mt-1" placeholder="Hospital or clinic name" data-testid="nurse-hospital" />
                      </div>
                      <div>
                        <Label>Specialization *</Label>
                        <Select value={formData.specialization} onValueChange={v => setFormData({...formData, specialization: v})}>
                          <SelectTrigger className="mt-1" data-testid="nurse-specialization"><SelectValue placeholder="Select specialization" /></SelectTrigger>
                          <SelectContent>
                            {nurseSpecializations.map(s => (
                              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="md:col-span-2">
                        <Label htmlFor="nurse-bio">About Yourself</Label>
                        <Textarea id="nurse-bio" value={formData.bio} onChange={e => setFormData({...formData, bio: e.target.value})} className="mt-1" rows={3} placeholder="Tell patients about your nursing experience..." data-testid="nurse-bio" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Section 3: Document Upload */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-[#0F62FE]">
                      <Upload className="w-5 h-5" />
                      3. Document Upload
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      {[
                        { id: 'nursing-cert', docType: 'license', title: 'Nursing Certificate', desc: 'GNM / BSc / ANM Certificate', accept: '.pdf,.jpg,.jpeg,.png' },
                        { id: 'reg-cert', docType: 'certificate', title: 'Registration Certificate', desc: 'State Nursing Council Registration', accept: '.pdf,.jpg,.jpeg,.png' },
                        { id: 'aadhar', docType: 'id_proof', title: 'Aadhar Card', desc: 'Government issued ID', accept: '.pdf,.jpg,.jpeg,.png' },
                        { id: 'photo', docType: 'photo', title: 'Passport Size Photo', desc: 'Recent photograph', accept: '.jpg,.jpeg,.png' },
                      ].map(doc => {
                        const uploaded = isDocUploaded(doc.docType) || uploadedDocs[doc.id];
                        const previewUrl = getDocUrl(doc.docType) || (uploadedDocs[doc.id] ? `${API_BASE}${uploadedDocs[doc.id]}` : null);
                        return (
                          <div key={doc.id} className={`p-4 rounded-xl border-2 transition-colors ${uploaded ? 'border-emerald-300 bg-emerald-50/30' : 'border-dashed border-slate-200 hover:border-[#0F62FE]'}`}>
                            <div className="text-center">
                              {uploaded && previewUrl && previewUrl.match(/\.(jpg|jpeg|png|webp)$/i) ? (
                                <img src={previewUrl} alt={doc.title} className="w-16 h-16 object-cover rounded-lg mx-auto mb-2 border border-slate-200" />
                              ) : (
                                <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                              )}
                              <h4 className="font-medium text-slate-900 text-sm">{doc.title}</h4>
                              <p className="text-xs text-slate-500 mb-2">{doc.desc}</p>
                              <input type="file" id={`${doc.id}-upload`} className="hidden" accept={doc.accept} onChange={e => {
                                handleUpload(doc.docType === 'photo' ? 'certificate' : doc.docType, e.target.files[0]).then(() => {
                                  if (doc.docType === 'photo' || doc.docType === 'certificate') {
                                    setUploadedDocs(prev => ({ ...prev, [doc.id]: prev[doc.docType === 'photo' ? 'certificate' : doc.docType] }));
                                  }
                                });
                              }} />
                              <div className="flex items-center justify-center gap-2">
                                <Button variant="outline" size="sm" onClick={() => document.getElementById(`${doc.id}-upload`).click()} data-testid={`${doc.id}-choose-btn`}>
                                  {uploaded ? 'Re-upload' : 'Choose File'}
                                </Button>
                                {uploaded && previewUrl && (
                                  <Button variant="outline" size="sm" className="text-blue-600 border-blue-200 hover:bg-blue-50" onClick={() => window.open(previewUrl, '_blank')} data-testid={`${doc.id}-preview-btn`}>
                                    <Eye className="w-3 h-3 mr-1" />
                                    View
                                  </Button>
                                )}
                              </div>
                              {uploaded && <p className="text-xs text-emerald-600 mt-1"><CheckCircle className="w-3 h-3 inline mr-1" />Uploaded</p>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Section 4: Services & Pricing */}
                <NurseServicesForm nurseServices={nurseServices} setNurseServices={setNurseServices} />

                {/* Section 5: Availability Details */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-[#0F62FE]">
                      <Clock className="w-5 h-5" />
                      5. Availability Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <Label className="mb-2 block">Available For</Label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {[
                            { id: 'home_visit', label: 'Home Visit', icon: Home },
                            { id: 'night_duty', label: 'Night Duty', icon: Clock },
                            { id: 'full_time', label: 'Full Time', icon: Activity },
                            { id: 'part_time', label: 'Part Time', icon: Clock },
                          ].map(svc => {
                            const isSelected = formData.services.includes(svc.id);
                            return (
                              <div key={svc.id} className={`flex items-center justify-between p-3 rounded-xl border-2 cursor-pointer transition-all ${isSelected ? 'bg-emerald-50 border-emerald-400 shadow-md shadow-emerald-100' : 'border-slate-200 hover:border-slate-300'}`} onClick={() => toggleService(svc.id)}>
                                <div className="flex items-center gap-2">
                                  <svc.icon className={`w-4 h-4 ${isSelected ? 'text-emerald-500' : 'text-slate-400'}`} />
                                  <span className={`text-sm font-medium ${isSelected ? 'text-emerald-700' : 'text-slate-600'}`}>{svc.label}</span>
                                </div>
                                <Checkbox 
                                  checked={isSelected}
                                  onCheckedChange={() => toggleService(svc.id)}
                                  className={isSelected ? 'border-emerald-400 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500' : ''}
                                  data-testid={`nurse-service-${svc.id}`}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label className="mb-2 block">Available Days</Label>
                          <div className="flex flex-wrap gap-2">
                            {daysOfWeek.map(day => (
                              <button key={day} type="button" className={`px-4 py-2 rounded-xl border-2 text-sm font-semibold transition-all ${formData.available_days.includes(day) ? 'bg-emerald-500 text-white border-emerald-500 shadow-md shadow-emerald-200 scale-105' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`} onClick={() => toggleDay(day)}>
                                {day}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <Label className="mb-2 block">Working Hours</Label>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs text-slate-500">Start</Label>
                              <Input type="time" value={formData.time_slots[0]?.start || '09:00'} onChange={e => setFormData({...formData, time_slots: [{...formData.time_slots[0], start: e.target.value}]})} className="mt-0.5" />
                            </div>
                            <div>
                              <Label className="text-xs text-slate-500">End</Label>
                              <Input type="time" value={formData.time_slots[0]?.end || '17:00'} onChange={e => setFormData({...formData, time_slots: [{...formData.time_slots[0], end: e.target.value}]})} className="mt-0.5" />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="nurse-area">Preferred Area / City</Label>
                          <Input id="nurse-area" value={formData.preferred_area} onChange={e => setFormData({...formData, preferred_area: e.target.value, location: e.target.value})} className="mt-1" placeholder="e.g. Mumbai, Thane" data-testid="nurse-preferred-area" />
                        </div>
                        <div>
                          <Label>Home Visit Fee (per visit)</Label>
                          <Input type="number" value={formData.home_visit_fee} onChange={e => setFormData({...formData, home_visit_fee: e.target.value})} className="mt-1" placeholder="e.g. 800" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Section 6: Bank Details */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-[#0F62FE]">
                      <DollarSign className="w-5 h-5" />
                      6. Bank Details (Payment ke liye)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="bank-holder">Account Holder Name</Label>
                        <Input id="bank-holder" value={formData.bank_account_holder} onChange={e => setFormData({...formData, bank_account_holder: e.target.value})} className="mt-1" placeholder="As per bank records" data-testid="nurse-bank-holder" />
                      </div>
                      <div>
                        <Label htmlFor="bank-name">Bank Name</Label>
                        <Input id="bank-name" value={formData.bank_name} onChange={e => setFormData({...formData, bank_name: e.target.value})} className="mt-1" placeholder="e.g. State Bank of India" data-testid="nurse-bank-name" />
                      </div>
                      <div>
                        <Label htmlFor="acc-number">Account Number</Label>
                        <Input id="acc-number" value={formData.account_number} onChange={e => setFormData({...formData, account_number: e.target.value})} className="mt-1" placeholder="Enter account number" data-testid="nurse-account-number" />
                      </div>
                      <div>
                        <Label htmlFor="ifsc">IFSC Code</Label>
                        <Input id="ifsc" value={formData.ifsc_code} onChange={e => setFormData({...formData, ifsc_code: e.target.value})} className="mt-1" placeholder="e.g. SBIN0001234" data-testid="nurse-ifsc" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Section 7: Declaration */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-[#0F62FE]">
                      <CheckCircle className="w-5 h-5" />
                      7. Declaration
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                      <Checkbox 
                        checked={formData.declaration_accepted}
                        onCheckedChange={v => setFormData({...formData, declaration_accepted: v})}
                        data-testid="nurse-declaration"
                        className="mt-1"
                      />
                      <p className="text-sm text-slate-700 leading-relaxed">
                        I hereby declare that all the information provided is true and correct to the best of my knowledge. I understand that providing false information may result in rejection of my registration and termination of my account.
                      </p>
                    </div>
                    <div className="mt-6 flex justify-end">
                      <Button 
                        className="bg-[#0F62FE] hover:bg-[#0050E6] px-8"
                        onClick={handleSaveProfile}
                        disabled={saving}
                        data-testid="nurse-save-profile"
                      >
                        {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Submit Registration</>}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              /* ============ DOCTOR COMPLETE PROFILE FORM (single page) ============ */
              <div className="space-y-6" data-testid="doctor-profile-form">
                {/* Section 1: Personal Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-[#0F62FE]">
                      <User className="w-5 h-5" />
                      1. Personal Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">Full Name *</Label>
                        <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="mt-1" placeholder="Enter full name" data-testid="doctor-name" />
                      </div>
                      <div>
                        <Label htmlFor="email">Email</Label>
                        <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="mt-1" placeholder="email@example.com" data-testid="doctor-email" />
                      </div>
                      <div>
                        <Label htmlFor="location">Location</Label>
                        <Input id="location" placeholder="City, State" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} className="mt-1" data-testid="doctor-location" />
                      </div>
                      <div>
                        <Label htmlFor="clinic_name">Clinic Name</Label>
                        <Input id="clinic_name" value={formData.clinic_name || ''} onChange={(e) => setFormData({ ...formData, clinic_name: e.target.value })} className="mt-1" placeholder="e.g. Sharma Health Clinic" data-testid="doctor-clinic-name" />
                      </div>
                      <div>
                        <Label htmlFor="clinic">Clinic Address</Label>
                        <Input id="clinic" value={formData.clinic_address} onChange={(e) => setFormData({ ...formData, clinic_address: e.target.value })} className="mt-1" placeholder="Clinic / Hospital address" data-testid="doctor-clinic" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Section 2: Professional Details */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-[#0F62FE]">
                      <FileText className="w-5 h-5" />
                      2. Professional Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Specialization *</Label>
                        <Select value={formData.specialization} onValueChange={(value) => setFormData({ ...formData, specialization: value })}>
                          <SelectTrigger className="mt-1" data-testid="doctor-specialization"><SelectValue placeholder="Select specialization" /></SelectTrigger>
                          <SelectContent>
                            {specializations.map((spec) => (
                              <SelectItem key={spec.value} value={spec.value}>{spec.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="qualification">Qualification *</Label>
                        <Input id="qualification" placeholder="MBBS, MD, etc." value={formData.qualification} onChange={(e) => setFormData({ ...formData, qualification: e.target.value })} className="mt-1" data-testid="doctor-qualification" />
                      </div>
                      <div>
                        <Label htmlFor="experience">Years of Experience</Label>
                        <Input id="experience" type="number" value={formData.experience_years} onChange={(e) => setFormData({ ...formData, experience_years: e.target.value })} className="mt-1" data-testid="doctor-experience" />
                      </div>
                      <div>
                        <Label htmlFor="med-reg">Registration Number *</Label>
                        <Input id="med-reg" value={formData.medical_registration_number} onChange={(e) => setFormData({ ...formData, medical_registration_number: e.target.value })} className="mt-1" placeholder="MCI / State Medical Council Registration Number" data-testid="doctor-med-reg" />
                      </div>
                      <div className="md:col-span-2">
                        <Label htmlFor="bio">Bio</Label>
                        <Textarea id="bio" placeholder="Tell patients about yourself..." value={formData.bio} onChange={(e) => setFormData({ ...formData, bio: e.target.value })} className="mt-1" rows={3} data-testid="doctor-bio" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Section 3: Availability & Fees */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-[#0F62FE]">
                      <Clock className="w-5 h-5" />
                      3. Availability & Fees
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <Label className="mb-2 block">Services Offered</Label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {[
                            // { id: 'online', label: 'Online Consultation', icon: Video },
                            { id: 'home_visit', label: 'Home Visit', icon: Home },
                            { id: 'clinic', label: 'Clinic Visit', icon: Building },
                          ].map(svc => {
                            const isSelected = formData.services.includes(svc.id);
                            return (
                              <div key={svc.id} className={`flex items-center justify-between p-3 rounded-xl border-2 cursor-pointer transition-all ${isSelected ? 'bg-red-50 border-red-400 shadow-md shadow-red-100' : 'border-slate-200 hover:border-slate-300'}`} onClick={() => toggleService(svc.id)}>
                                <div className="flex items-center gap-2">
                                  <svc.icon className={`w-4 h-4 ${isSelected ? 'text-red-500' : 'text-slate-400'}`} />
                                  <span className={`text-sm font-medium ${isSelected ? 'text-red-700' : 'text-slate-600'}`}>{svc.label}</span>
                                </div>
                                <Checkbox checked={isSelected} onCheckedChange={() => toggleService(svc.id)} className={isSelected ? 'border-red-400 data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500' : ''} data-testid={`doctor-service-${svc.id}`} />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label className="mb-2 block">Available Days</Label>
                          <div className="flex flex-wrap gap-2">
                            {daysOfWeek.map((day) => (
                              <button key={day} type="button" className={`px-4 py-2 rounded-xl border-2 text-sm font-semibold transition-all ${formData.available_days.includes(day) ? 'bg-emerald-500 text-white border-emerald-500 shadow-md shadow-emerald-200 scale-105' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`} onClick={() => toggleDay(day)}>
                                {day}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <Label className="mb-2 block">Working Hours</Label>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs text-slate-500">Start</Label>
                              <Input type="time" value={formData.time_slots[0]?.start || '09:00'} onChange={(e) => setFormData({...formData, time_slots: [{ ...formData.time_slots[0], start: e.target.value }]})} className="mt-0.5" />
                            </div>
                            <div>
                              <Label className="text-xs text-slate-500">End</Label>
                              <Input type="time" value={formData.time_slots[0]?.end || '17:00'} onChange={(e) => setFormData({...formData, time_slots: [{ ...formData.time_slots[0], end: e.target.value }]})} className="mt-0.5" />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Consultation Fee (₹)</Label>
                          <Input type="number" value={formData.consultation_fee} onChange={(e) => setFormData({ ...formData, consultation_fee: e.target.value })} className="mt-1" placeholder="e.g. 500" data-testid="doctor-consultation-fee" />
                        </div>
                        <div>
                          <Label>Home Visit Fee (₹)</Label>
                          <Input type="number" value={formData.home_visit_fee} onChange={(e) => setFormData({ ...formData, home_visit_fee: e.target.value })} className="mt-1" placeholder="e.g. 1000" data-testid="doctor-homevisit-fee" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Section 4: Documents */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-[#0F62FE]">
                      <Upload className="w-5 h-5" />
                      4. Verification Documents
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      {[
                        { id: 'doc-id', docType: 'id_proof', title: 'ID Proof', desc: 'Aadhar Card / PAN Card', accept: '.pdf,.jpg,.jpeg,.png' },
                        { id: 'doc-cert', docType: 'certificate', title: 'Degree Certificate', desc: 'MBBS / MD Certificate', accept: '.pdf,.jpg,.jpeg,.png' },
                        { id: 'doc-reg-cert', docType: 'registration_cert', title: 'Registration Certificate', desc: 'Medical Council Registration Certificate', accept: '.pdf,.jpg,.jpeg,.png' },
                        { id: 'doc-photo', docType: 'photo', title: 'Passport Size Photo', desc: 'Recent photograph', accept: '.jpg,.jpeg,.png' },
                      ].map(doc => {
                        const uploaded = isDocUploaded(doc.docType) || uploadedDocs[doc.id];
                        const previewUrl = getDocUrl(doc.docType) || (uploadedDocs[doc.id] ? `${API_BASE}${uploadedDocs[doc.id]}` : null);
                        return (
                          <div key={doc.id} className={`p-4 rounded-xl border-2 transition-colors ${uploaded ? 'border-emerald-300 bg-emerald-50/30' : 'border-dashed border-slate-200 hover:border-[#0F62FE]'}`}>
                            <div className="text-center">
                              {uploaded && previewUrl && previewUrl.match(/\.(jpg|jpeg|png|webp)$/i) ? (
                                <img src={previewUrl} alt={doc.title} className="w-16 h-16 object-cover rounded-lg mx-auto mb-2 border border-slate-200" />
                              ) : (
                                <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                              )}
                              <h4 className="font-medium text-slate-900 text-sm">{doc.title}</h4>
                              <p className="text-xs text-slate-500 mb-2">{doc.desc}</p>
                              <input type="file" id={`${doc.id}-upload`} className="hidden" accept={doc.accept} onChange={(e) => {
                                handleUpload(doc.docType, e.target.files[0]).then(() => {
                                  setUploadedDocs(prev => ({ ...prev, [doc.id]: prev[doc.docType] }));
                                });
                              }} />
                              <div className="flex items-center justify-center gap-2">
                                <Button variant="outline" size="sm" onClick={() => document.getElementById(`${doc.id}-upload`).click()} data-testid={`${doc.id}-choose-btn`}>
                                  {uploaded ? 'Re-upload' : 'Choose File'}
                                </Button>
                                {uploaded && previewUrl && (
                                  <Button variant="outline" size="sm" className="text-blue-600 border-blue-200 hover:bg-blue-50" onClick={() => window.open(previewUrl, '_blank')} data-testid={`${doc.id}-preview-btn`}>
                                    <Eye className="w-3 h-3 mr-1" />
                                    View
                                  </Button>
                                )}
                              </div>
                              {uploaded && <p className="text-xs text-emerald-600 mt-1"><CheckCircle className="w-3 h-3 inline mr-1" />Uploaded</p>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Save Button */}
                <div className="flex justify-end">
                  <Button className="bg-[#0F62FE] hover:bg-[#0050E6] px-8" onClick={handleSaveProfile} disabled={saving} data-testid="doctor-save-profile">
                    {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Profile</>}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {user?.role === 'doctor' && (
            <TabsContent value="blogs">
              <BlogManager />
            </TabsContent>
          )}
        </Tabs>

        {/* Appointment Detail Modal */}
        {selectedApt && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedApt(null)}>
            <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="apt-detail-modal">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-900">Appointment Details</h2>
                <Button variant="ghost" size="sm" onClick={() => setSelectedApt(null)} data-testid="close-apt-modal">
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="p-6 space-y-4">
                {/* Patient Info */}
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                  <div className="w-12 h-12 rounded-full bg-[#E0F2FE] flex items-center justify-center font-bold text-[#0F62FE]">
                    {getInitials(selectedApt.patient?.name || 'P')}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{selectedApt.patient?.name || 'Patient'}</p>
                    <p className="text-sm text-slate-500">{selectedApt.patient?.phone || ''}</p>
                  </div>
                </div>

                {/* Appointment Info */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg border border-slate-100">
                    <p className="text-xs text-slate-500">Date</p>
                    <p className="font-medium text-slate-900">{selectedApt.appointment_date}</p>
                  </div>
                  <div className="p-3 rounded-lg border border-slate-100">
                    <p className="text-xs text-slate-500">Time</p>
                    <p className="font-medium text-slate-900">{selectedApt.time_slot}</p>
                  </div>
                  <div className="p-3 rounded-lg border border-slate-100">
                    <p className="text-xs text-slate-500">Service</p>
                    <p className="font-medium text-slate-900 capitalize">{selectedApt.service_type?.replace('_', ' ')}</p>
                  </div>
                  <div className="p-3 rounded-lg border border-slate-100">
                    <p className="text-xs text-slate-500">Fee</p>
                    <p className="font-medium text-slate-900">₹{selectedApt.fee || 0}</p>
                  </div>
                  <div className="p-3 rounded-lg border border-slate-100">
                    <p className="text-xs text-slate-500">Status</p>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                      selectedApt.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' :
                      selectedApt.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                      selectedApt.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                      'bg-red-100 text-red-700'
                    }`}>{selectedApt.status}</span>
                  </div>
                  <div className="p-3 rounded-lg border border-slate-100">
                    <p className="text-xs text-slate-500">Payment</p>
                    <p className="font-medium text-slate-900 capitalize">{selectedApt.payment_status || 'pending'}</p>
                  </div>
                </div>

                {/* Address (for home visit) */}
                {selectedApt.service_type === 'home_visit' && selectedApt.address && (
                  <div className="p-3 rounded-lg border border-red-100 bg-red-50/50">
                    <p className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-red-500" />Visit Address</p>
                    <p className="font-medium text-slate-900 mt-0.5">{selectedApt.address}</p>
                  </div>
                )}

                {/* Symptoms */}
                {selectedApt.symptoms && (
                  <div className="p-3 rounded-lg border border-slate-100">
                    <p className="text-xs text-slate-500">Symptoms</p>
                    <p className="text-slate-700 mt-0.5">{selectedApt.symptoms}</p>
                  </div>
                )}

                {/* Notes */}
                {selectedApt.notes && (
                  <div className="p-3 rounded-lg border border-slate-100">
                    <p className="text-xs text-slate-500">Notes</p>
                    <p className="text-slate-700 mt-0.5">{selectedApt.notes}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
